import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormHelperText,
  InputAdornment,
  Link,
  Stack,
  SvgIcon,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import viewpaths from "global/constants/ViewPathConstants";
import { useAppSelector } from "hooks/use-app-select";
import { useEffect, useState } from "react";
import { loginAction, selectAuthenticated, selectLastVisitedLink, setLastVisitedLink } from "redux/authSlice";
import history from "utils/history";
import AuthLayout from "layouts/AuthLayout/AuthLayout";
import { login } from "screens/Auth/AuthService";
import { useAppDispatch } from "hooks/use-app-dispatch";
import {
  convertResourceToObjectFormat,
  handleErrorMessage,
  isTruthy,
  openErrorNotification,
} from "helpers/methods";
import urls from "global/constants/UrlConstants";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import strings from "global/constants/StringConstants";
import { CustomInput } from "global/components";
import { useAuthRouteRedirection } from "layouts/Config";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import defaultImage from "assets/icons1/avatars/default.png";
import { updatePreference } from "redux/preferencesSlice";
import Logo from "assets/images/light_side_nav_mailzzy_logo.svg";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import authStyles from "./Auth.styles";
import {
  handleLoginValidation,
  loginFormField,
} from "./AuthTypesAndValidation";
import _ from "lodash";
import pandaIcon from "assets/icons1/panda_icon.svg";
import mobilePandaIcon from "assets/icons1/mobile_panda_icon.svg";
import { getCredentialOptions } from "screens/Settings/SettingsService";
import Clarity from "@microsoft/clarity";

const Login = () => {
  useTitle(title.App);
  const classes = authStyles;
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const emailRegex = strings.regex;
  const isLoggedIn = useAppSelector(selectAuthenticated);
  const authRoute = useAuthRouteRedirection();
  const [processing, setProcessing] = useState<boolean>(false);
  const [credentials, setCredentials] = useState(loginFormField);
  const [visibility, setVisibility] = useState(false);
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
  const [isPasskeyAvailable, setIsPasskeyAvailable] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState<boolean>(false);
  const current = useAppSelector(selectLastVisitedLink);

  const checkPasskeyAvailability = async () => {
    if (window.PublicKeyCredential) {
      const isUserVerifyingAvailable =
        await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      if (isUserVerifyingAvailable) {
        setIsPasskeyAvailable(true);
      }
    }
  };

  useEffect(() => {
    checkPasskeyAvailability();
  }, []);

  const handleChange = (event: any) => {
    setCredentials({
      ...credentials,
      [event.target.name]: {
        value: event.target.value,
        error: "",
      },
    });
  };

  const doLogin = async () => {
    try {
      if (handleLoginValidation(credentials).isValid) {
        setProcessing(true);
        let captchaToken: any;
        let userType: any;
        if (urls.PROD) {
          captchaToken = await executeRecaptcha!();
        }
        const user = await login(
          credentials.email.value,
          credentials.password.value,
          captchaToken
        );
        if (user.multiFactorLogin) {
          history.push({
            pathname: viewpaths.verifyTOTPViewPath,
            state: {
              email: user.email,
            },
          });
          return;
        }
        if (user.mfaSetupRequired) {
          history.push({
            pathname: viewpaths.setupMFAViewPath,
            state: {
              email: user.email,
              password: credentials.password.value,
              token: user.authToken,
            },
          });
          return;
        }
        const formattedResources = convertResourceToObjectFormat(
          user.resources
        );
        if (isTruthy(formattedResources)) {
          dispatch(
            loginAction({
              authenticated: true,
              accessToken: user.authToken,
              refreshToken: user.refreshToken,
              userName: user.name,
              email: (user.email ?? "").toLowerCase(),
              userRole: user.role,
              resources: formattedResources,
              profileImage: user.profilePicture
                ? "data:image/png;base64," + user.profilePicture
                : defaultImage,
              type: userType?.message ?? "",
              deviceId: "",
              account: user.account,
              userId: user.id,
              isTrial: user.trial,
            })
          );
          if (urls.PROD) {
            Clarity.setTag("account", user.account);
            Clarity.setTag("email", user.email);
            Clarity.identify(user.email, user.account, user.id, user.account);
          }
          dispatch(
            updatePreference({
              layout: "vertical",
              theme: "dark",
            })
          );
          history.push(current);
        } else {
          openErrorNotification("Something went wrong.");
        }
        setProcessing(false);
      }
    } catch (error: any) {
      handleErrorMessage(error);
      setProcessing(false);
    }
  };

  const handleLoginUsingPasskey = async () => {
    let timeout: NodeJS.Timeout | null = null;
    try {
      setPasskeyLoading(true);
      timeout = setTimeout(() => {
        setPasskeyLoading(false);
        openErrorNotification("Passkey operation timed out.");
      }, 30000);
      let userType: any;
      const user = await getCredentialOptions();
      if (timeout) clearTimeout(timeout);

      if (user) {
        const formattedResources = convertResourceToObjectFormat(
          user.resources
        );

        if (isTruthy(formattedResources)) {
          dispatch(
            loginAction({
              authenticated: true,
              accessToken: user.authToken,
              refreshToken: user.refreshToken,
              userName: user.name,
              email: (user.email ?? "").toLowerCase(),
              userRole: user.role,
              resources: formattedResources,
              profileImage: user.profilePicture
                ? "data:image/png;base64," + user.profilePicture
                : defaultImage,
              type: userType?.message ?? "",
              deviceId: "",
              account: user.account,
              userId: user.id,
              isTrial: user.trial,
            })
          );
          dispatch(
            updatePreference({
              layout: "vertical",
              theme: "dark",
            })
          );
        } else {
          openErrorNotification("Something went wrong.");
        }
        history.push(viewpaths.dashboardViewPath);
      }
    } catch (error: any) {
      if (timeout) clearTimeout(timeout);
      handleErrorMessage(error);
    } finally {
      if (timeout) clearTimeout(timeout);
      setPasskeyLoading(false);
    }
  };

  const handleKeypress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      doLogin();
    }
  };

  const registerPage = () => {
    history.push(viewpaths.registerViewPath);
  };

  const loginScreen = () => {
    return (
      <Box sx={{ position: "relative" }}>
        <Box
          component="img"
          src={mdUp ? pandaIcon : mobilePandaIcon}
          alt="Panda"
          sx={{
            position: "absolute",
            width: mdUp ? "150px" : "100px",
            height: "auto",
            right: mdUp ? "-86px" : "50%",
            left: mdUp ? "auto" : "50%",
            top: mdUp ? "50%" : "-70px",
            transform: mdUp ? "translateY(-50%)" : "translateX(-50%)",
            zIndex: 2,
          }}
        />
        <Card elevation={20} sx={classes.cardBox}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              p: "30px",
              pt: "12px",
            }}
          >
            <img src={Logo} width="180px" />
          </Box>
          <CardHeader
            subheader={
              <Typography
                color="text.primary"
                variant="body2"
                textAlign={"center"}
                gutterBottom
              >
                New around here? Create an account.
                <span
                  onClick={registerPage}
                  style={{
                    cursor: "pointer",
                    fontWeight: "bolder",
                    color: theme.palette.neutral[700],
                    marginLeft: 4,
                    marginRight: 4,
                    textDecoration: "underline",
                  }}
                >
                  Join Mailzzy
                </span>{" "}
                for free 30 days trial.
              </Typography>
            }
            sx={{ p: 0 }}
            title={
              <Typography
                color="text.primary"
                variant="h5"
                textAlign={"center"}
                gutterBottom
              >
                Log In
              </Typography>
            }
          />
          <CardContent>
            <Stack spacing={3}>
              <Box>
                <CustomInput
                  required
                  label="Business Email"
                  autoFocus
                  placeHolder="Enter Business Email"
                  name="email"
                  onChange={handleChange}
                  type="email"
                  value={credentials.email.value}
                  disabled={processing}
                  propsToInputElement={{ maxLength: 50 }}
                  error={
                    !isTruthy(credentials.email.value) &&
                    credentials.email.error
                  }
                  onKeyPress={handleKeypress}
                  helperText={
                    !isTruthy(credentials.email.value)
                      ? credentials.email.error
                      : ""
                  }
                />
                {!emailRegex.test(credentials.email.value) &&
                  credentials.email.value.length > 0 && (
                    <FormHelperText error sx={{ paddingLeft: "14px" }}>
                      Please enter valid email
                    </FormHelperText>
                  )}
              </Box>
              <CustomInput
                required
                label="Password"
                placeHolder="••••••••"
                name="password"
                onChange={handleChange}
                type={visibility ? "text" : "password"}
                value={credentials.password.value}
                disabled={processing}
                onKeyPress={handleKeypress}
                InputProps={{
                  endAdornment: (
                    <InputAdornment
                      position="start"
                      onClick={() => setVisibility(!visibility)}
                      sx={{ cursor: "pointer" }}
                    >
                      <SvgIcon>
                        {visibility ? (
                          <VisibilityOutlinedIcon />
                        ) : (
                          <VisibilityOffOutlinedIcon />
                        )}
                      </SvgIcon>
                    </InputAdornment>
                  ),
                }}
                propsToInputElement={{ maxLength: 30 }}
                error={
                  credentials.password.value.length < 8
                    ? credentials.password.error
                    : null
                }
                helperText={credentials.password.error}
              />
            </Stack>
            <Stack
              direction={{
                xl: "row",
                lg: "row",
                md: "row",
                sm: "column",
                xs: "column",
              }}
              mt={2}
              alignItems={{
                sm: "start",
                xs: "start",
              }}
              justifyContent="space-between"
              display="flex"
            >
              {/* <FormControlLabel
              control={<Checkbox size="large" />}
              label="Stay signed in for 30 days"
              sx={{
                "&.Mui-checked": {
                  color: "green",
                },
              }}
            /> */}

              <Box
                sx={{
                  cursor: "pointer",
                  width: "100%",
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <Link
                  underline="hover"
                  variant="subtitle2"
                  color="text.primary"
                  fontWeight={600}
                  onClick={() => history.push(viewpaths.forgotPasswordViewPath)}
                >
                  Forgot password?
                </Link>
              </Box>
            </Stack>
            <Stack
              direction="column"
              justifyContent="center"
              display={"flex"}
              alignItems={"center"}
            >
              <Button
                fullWidth
                size="medium"
                sx={{ mt: 4 }}
                type="submit"
                variant="contained"
                onClick={doLogin}
                disabled={
                  _.isEmpty(credentials.email.value) ||
                  _.isEmpty(credentials.password.value) ||
                  !emailRegex.test(credentials.email.value) ||
                  credentials.password?.value?.length < 8 ||
                  processing
                }
              >
                Log In
              </Button>
              {isPasskeyAvailable && (
                <>
                  <Divider
                    sx={{
                      "&:before, &:after": {
                        border: "1px solid rgba(0, 0, 0, 0.12)",
                        width: "70px",
                      },
                      margin: "20px 0px 4px 0px",
                    }}
                  >
                    <Typography>OR</Typography>
                  </Divider>

                  <Button
                    fullWidth
                    size="medium"
                    sx={{ mt: 3 }}
                    type="submit"
                    variant="contained"
                    onClick={handleLoginUsingPasskey}
                    disabled={passkeyLoading}
                  >
                    Login Using Passkey
                  </Button>
                </>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  };

  if (isLoggedIn) {
    history.push(authRoute);
    return null;
  } else {
    return (
      <AuthLayout>
        {loginScreen()}
        <Typography variant="body2" fontSize="11px" mt={2} textAlign="center">
          This site is protected by reCAPTCHA and the Google{" "}
          <a href="https://policies.google.com/privacy" target="_blank">
            <span>Privacy Policy</span>
          </a>{" "}
          and{" "}
          <a href="https://policies.google.com/terms" target="_blank">
            <span>Terms of Service</span>
          </a>{" "}
          apply.
        </Typography>
      </AuthLayout>
    );
  }
};

export default Login;
